import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Globe, TrendingUp, Zap } from 'lucide-react';

const BRAND = '#842014';
const BRAND_BG = '#fdf3f2';

const s = {
  section: {
    padding: '80px 0',
    backgroundColor: '#f9fafb',
    borderTop: '1px solid #f3f4f6',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 24px',
  },
  headingWrap: {
    textAlign: 'center',
    maxWidth: '768px',
    margin: '0 auto 64px auto',
  },
  h2: {
    fontSize: 'clamp(1.875rem, 3vw, 2.25rem)',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '16px',
    lineHeight: 1.2,
  },
  brandSpan: { color: BRAND },
  subtitle: {
    fontSize: '1.125rem',
    color: '#4b5563',
    lineHeight: 1.6,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '32px',
  },
  card: {
    backgroundColor: '#fff',
    padding: '32px',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #f3f4f6',
    transition: 'box-shadow 0.2s',
    cursor: 'default',
  },
  cardHover: {
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  },
  iconWrapper: {
    width: '64px',
    height: '64px',
    backgroundColor: BRAND_BG,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    transition: 'transform 0.3s',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '12px',
  },
  cardDesc: {
    color: '#6b7280',
    lineHeight: 1.625,
    margin: 0,
  },
};

const features = [
  {
    icon: <Gift size={32} color={BRAND} />,
    title: 'Rewards Programs',
    description: 'Integrate your products into our rewards ecosystem, giving families incentives while driving your sales.',
  },
  {
    icon: <Globe size={32} color={BRAND} />,
    title: 'Community Reach',
    description: 'Access a highly engaged community of parents, educators, and camp directors across the country.',
  },
  {
    icon: <TrendingUp size={32} color={BRAND} />,
    title: 'Brand Exposure',
    description: 'Get featured in our newsletters, social media campaigns, and directly on the CampsEZ platform.',
  },
  {
    icon: <Zap size={32} color={BRAND} />,
    title: 'Easy Integration',
    description: 'Our partnership team makes it simple to launch campaigns, sweepstakes, or promotional activities quickly.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function FeatureCard({ feature }) {
  const [hovered, setHovered] = useState(false);
  const [iconHovered, setIconHovered] = useState(false);

  return (
    <motion.div
      variants={itemVariants}
      style={{ ...s.card, ...(hovered ? s.cardHover : {}) }}
      onMouseEnter={() => { setHovered(true); setIconHovered(true); }}
      onMouseLeave={() => { setHovered(false); setIconHovered(false); }}
    >
      <div style={{ ...s.iconWrapper, transform: iconHovered ? 'scale(1.1)' : 'scale(1)' }}>
        {feature.icon}
      </div>
      <h3 style={s.cardTitle}>{feature.title}</h3>
      <p style={s.cardDesc}>{feature.description}</p>
    </motion.div>
  );
}

export function WhyPartner() {
  return (
    <section style={s.section}>
      <div style={s.container}>
        <div style={s.headingWrap}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={s.h2}
          >
            Why Partner With Camps<span style={s.brandSpan}>EZ</span>?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            style={s.subtitle}
          >
            We offer unique opportunities to connect with families in meaningful ways beyond traditional advertising.
          </motion.p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          style={s.grid}
        >
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
